import { useEffect, useRef, useState } from 'react';
import { Button, Upload } from 'antd';

import { isAttachAdmin } from '../../utils';
import { deleteHeldFile, uploadHeldFile } from '../../services/attachments';
import { FILE_LIST_LIMIT, fileFactsFor, fileIdsOf } from '../../utils/fileValues';
import { PHOTO_ACCEPT, fileRefusalWords, precheckFile, serverFileRefusal } from '../../utils/fileUpload';
import { normalizeId } from '../../utils/ids';
import { useOptionalHeldFiles } from '../../hooks/useHeldFiles';
import { PLUS, UPLOAD } from '../icons';
import FileUploadItem from './FileUploadItem';

/**
 * A file element's input in the item form (docs/plans/data-types-file-element.md
 * §3, §6; the 10b contract). antd `Upload`: picture cards for "Photos only",
 * a list for any file. A picked file uploads AT ONCE to the collection's held
 * files (with the card and the element, so the server refuses a wrong kind or
 * a file over the limit before the bytes land), shows its progress, then its
 * held thumbnail. The value it emits is the id — or, under "Allow multiple",
 * the ordered ids (at most 20; Move earlier / later reorders).
 *
 * Remove takes a file out of the value and shows it as "Removed when you
 * save", with Undo: nothing is deleted before Save. The form's held-file
 * books (hooks/useHeldFiles) delete an upload that is never saved; outside a
 * form they are this field's own, and the server reaps an unsaved upload
 * within the hour.
 *
 * Refusals read at the element: the form's own early ones (a blocked type, a
 * file over the limit, a non-photo under "Photos only", past 20) in the same
 * words as the server's codes, the server's in its own words.
 *
 * A FILE COLUMN of a repeating group (KPR-934; keepr-api KPR-750) is this same
 * field, once per entry. `group` names the repeating group and `entryKey` the
 * entry (its id, or the form's key for one not saved yet): the upload then
 * names the group as `element` and the column beside it, and the form's held
 * books are kept per entry and column, so an upload into one entry is never
 * another entry's, nor a top-level element's of the same name. `item` is the
 * item itself (its `files` facts), never the entry.
 */
export default function FileUploadField({ id, element, value, onChange, collectionId, cardId, item = null, storedValue, disabled = false, group = null, entryKey = null }) {
	const held = useOptionalHeldFiles();
	const localRef = useRef(new Map());
	const [, setLocalVersion] = useState(0);
	const name = element?.name;
	// What the upload names, and the key the form's held books file it under.
	const uploadElement = group || name;
	const uploadColumn = group ? name : undefined;
	const scope = group ? `${group}\u0000${entryKey ?? ''}\u0000${name}` : name;
	const multiple = element?.options?.allowMultiple === true;
	const picture = element?.options?.accept === 'image';
	const ids = fileIdsOf(value);
	const valueRef = useRef(ids);
	valueRef.current = ids;
	const [uploads, setUploads] = useState([]);
	const uploadsRef = useRef(uploads);
	uploadsRef.current = uploads;
	const [refusal, setRefusal] = useState(null);
	const mountedRef = useRef(true);
	// Set true again on mount: StrictMode (the app's development build) mounts,
	// unmounts and mounts again, and a ref left false would never clear an upload's
	// progress row or show its refusal.
	useEffect(() => {
		mountedRef.current = true;
		return () => { mountedRef.current = false; };
	}, []);

	const lookup = fileId => held?.lookup(fileId) || localRef.current.get(fileId) || null;
	const sessionUploads = held
		? held.uploadsFor(scope)
		: [...localRef.current.entries()].filter(([, entry]) => entry.element === scope).map(([fileId]) => fileId);
	const removedIds = [...new Set([...fileIdsOf(storedValue), ...sessionUploads])].filter(fileId => !ids.includes(fileId));

	const entryFor = (fileId, state) => {
		const known = lookup(fileId);
		if (known) return { uid: fileId, id: fileId, file: known.facts, source: known.source, state };
		const facts = fileFactsFor(item, fileId);
		if (facts) return { uid: fileId, id: fileId, file: facts, source: { itemId: normalizeId(item?._id) }, state };
		return { uid: fileId, id: fileId, file: null, source: null, state: state === 'ready' ? 'unknown' : state };
	};
	const entries = [
		...ids.map(fileId => entryFor(fileId, 'ready')),
		...removedIds.map(fileId => entryFor(fileId, 'removed')),
		...uploads.map(upload => ({ ...upload, state: 'uploading' })),
	];
	const byUid = new Map(entries.map(entry => [entry.uid, entry]));

	const emit = (nextIds) => {
		valueRef.current = nextIds;
		setRefusal(null);
		onChange(name, multiple ? nextIds : (nextIds[0] || ''));
	};
	const patchUploads = change => setUploads((current) => {
		const next = change(current);
		uploadsRef.current = next;
		return next;
	});

	const beforeUpload = (file, batch) => {
		const index = Math.max(0, (batch || []).indexOf(file));
		const count = valueRef.current.length + uploadsRef.current.length + index;
		const problem = precheckFile(file, element, { count, attachAdmin: isAttachAdmin() });
		if (problem) {
			setRefusal(problem.words);
			return Upload.LIST_IGNORE;
		}
		setRefusal(null);
		return true;
	};

	const customRequest = ({ file, onProgress, onSuccess, onError }) => {
		const uid = file.uid;
		patchUploads(current => [...current, { uid, name: file.name, percent: 0 }]);
		const progress = (percent) => {
			patchUploads(current => current.map(upload => (upload.uid === uid ? { ...upload, percent } : upload)));
			onProgress?.({ percent });
		};
		uploadHeldFile(collectionId, { file, cardId, element: uploadElement, column: uploadColumn }, { onProgress: progress })
			.then((created) => {
				const fileId = normalizeId(created?._id);
				// Gone: the form was left while this one was on its way, so nobody
				// will save it. Inside a form, an input that merely unmounted (an
				// entry folded away mid-upload) leaves the upload the form's.
				const gone = group && held && typeof held.alive === 'function' ? !held.alive() : !mountedRef.current;
				if (gone) {
					if (fileId) deleteHeldFile(collectionId, fileId).catch(() => {});
					return;
				}
				if (held) held.register(created, scope);
				else {
					localRef.current.set(fileId, { facts: { ...created, _id: fileId }, source: { heldIn: normalizeId(collectionId) }, element: scope });
					setLocalVersion(v => v + 1);
				}
				if (group && !mountedRef.current) {
					// This input is gone (its entry folded) and a new one may have
					// changed the column since: add the file to the column's value
					// AS IT IS NOW, never to the copy this instance last rendered.
					onChange(name, (live) => {
						const others = fileIdsOf(live).filter(entry => entry !== fileId);
						return multiple ? [...others, fileId] : fileId;
					});
					onSuccess?.(created);
					return;
				}
				const current = valueRef.current.filter(entry => entry !== fileId);
				emit(multiple ? [...current, fileId] : [fileId]);
				onSuccess?.(created);
			})
			.catch((error) => {
				if (mountedRef.current) setRefusal(serverFileRefusal(error, element));
				onError?.(error);
			})
			.finally(() => {
				if (mountedRef.current) patchUploads(current => current.filter(upload => upload.uid !== uid));
			});
	};

	const move = (fileId, delta) => {
		const list = [...ids];
		const from = list.indexOf(fileId);
		const to = from + delta;
		if (from < 0 || to < 0 || to >= list.length) return;
		[list[from], list[to]] = [list[to], list[from]];
		emit(list);
	};
	const undo = (fileId) => {
		if (multiple && ids.length >= FILE_LIST_LIMIT) {
			setRefusal(fileRefusalWords('too_many_files', { element, limit: FILE_LIST_LIMIT }));
			return;
		}
		emit(multiple ? [...ids, fileId] : [fileId]);
	};

	const readyCount = ids.length + uploads.length;
	const full = multiple ? readyCount >= FILE_LIST_LIMIT : readyCount >= 1;
	const offline = disabled || !normalizeId(collectionId);
	const trigger = picture ? (
		<button type="button" className="file-upload__add">
			<PLUS aria-hidden="true" />
			<span>{multiple ? 'Add photos' : 'Add photo'}</span>
		</button>
	) : <Button icon={<UPLOAD />}>{multiple ? 'Add files' : 'Add a file'}</Button>;
	const readyIds = entries.filter(entry => entry.state === 'ready' || entry.state === 'unknown').map(entry => entry.id);

	return (
		<div className={`file-upload${picture ? ' file-upload--pictures' : ''}`} data-testid="file-upload">
			<Upload
				id={id}
				listType={picture ? 'picture-card' : 'text'}
				accept={picture ? PHOTO_ACCEPT : undefined}
				multiple={multiple}
				fileList={entries.map(entry => ({
					uid: entry.uid,
					name: entry.file?.originalName || entry.name || 'File',
					status: entry.state === 'uploading' ? 'uploading' : 'done',
					percent: entry.percent,
				}))}
				beforeUpload={beforeUpload}
				customRequest={customRequest}
				disabled={offline}
				itemRender={(_node, listed) => {
					const entry = byUid.get(listed.uid);
					if (!entry) return null;
					const index = readyIds.indexOf(entry.id);
					return (
						<FileUploadItem
							entry={entry}
							picture={picture}
							disabled={disabled}
							canMove={multiple && readyIds.length > 1}
							isFirst={index === 0}
							isLast={index === readyIds.length - 1}
							onRemove={() => emit(ids.filter(fileId => fileId !== entry.id))}
							onUndo={() => undo(entry.id)}
							onMove={delta => move(entry.id, delta)}
						/>
					);
				}}
			>
				{full || offline ? null : trigger}
			</Upload>
			{refusal ? <div className="element-field__error" role="alert">{refusal}</div> : null}
		</div>
	);
}
